import React, { useState } from 'react';
import { useConfiguration } from '../hooks/useConfiguration';
import { usePricing } from '../hooks/usePricing';
import StepNavigation from './StepNavigation';
import OptionSelector from './OptionSelector';
import ProductPreview from './ProductPreview';
import PartsList from './PartsList';
import PricingSummary from './PricingSummary';
import QuoteRequestModal from './QuoteRequestModal';
import './Configurator.css';

const Configurator = () => {
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const {
    rules,
    configuration,
    currentStep,
    loading,
    error,
    updateConfiguration,
    updateMultiSelectConfiguration,
    nextStep,
    previousStep,
    goToStep,
    resetConfiguration,
    canProceed
  } = useConfiguration();

  const { pricing, formatPrice } = usePricing(configuration);

  if (loading) {
    return (
      <div className="configurator-container">
        <div className="loading">Loading configurator...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="configurator-container">
        <div className="error">{error}</div>
      </div>
    );
  }

  if (!rules) {
    return null;
  }

  const currentStepData = rules.steps[currentStep];
  const totalSteps = rules.steps.length;
  const isLastStep = currentStep >= totalSteps - 1;
  const partsCount = pricing?.parts?.length || 0;

  const handlePartsListClick = () => {
    const target = document.getElementById('parts-list-section');
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="configurator-container">
      <div className="configurator-content">
        <div className="configurator-left">
          <div className="now-building-header">
            <h2>Now Building:</h2>
            <div className="parts-price-info">
              <button type="button" className="parts-list-button" onClick={handlePartsListClick}>
                <span role="img" aria-hidden="true">📋</span>
                Parts List ({partsCount})
              </button>
              <span className="current-price">
                {pricing ? formatPrice(pricing.totalPrice) : '$0.00'}
              </span>
            </div>
          </div>

          <div className="configuration-panel">
            <div className="step-header">
              <div className="step-number">{currentStep + 1}</div>
              <h3>{currentStepData.name}</h3>
            </div>

            <OptionSelector
              step={currentStepData}
              configuration={configuration}
              onSelect={updateConfiguration}
              onMultiSelect={updateMultiSelectConfiguration}
            />

            <StepNavigation
              currentStep={currentStep}
              totalSteps={totalSteps}
              onNext={isLastStep ? () => setShowQuoteModal(true) : nextStep}
              onPrevious={previousStep}
              canProceed={canProceed()}
              isOptional={!currentStepData.required}
            />
          </div>

          <div className="configurator-actions">
            <button className="help-button" onClick={() => setShowHelpModal(true)} title="Get help">
              ❓ Help
            </button>
            <button className="reset-button" onClick={resetConfiguration}>
              Reset Configurator
            </button>
          </div>
        </div>

        <div className="configurator-right">
          <ProductPreview
            configuration={configuration}
            currentStep={currentStepData}
          />

          <div className="add-to-cart-section">
            <button
              className="request-quote-button"
              onClick={() => setShowQuoteModal(true)}
            >
              Request Quote
            </button>
          </div>
        </div>
      </div>

      {pricing && (
        <>
          <PartsList parts={pricing.parts} />
          <PricingSummary pricing={pricing} formatPrice={formatPrice} />
        </>
      )}

      <QuoteRequestModal
        isOpen={showQuoteModal}
        onClose={() => setShowQuoteModal(false)}
        configuration={configuration}
        pricing={pricing}
      />

      {showHelpModal && (
        <div className="modal-overlay" onClick={() => setShowHelpModal(false)}>
          <div className="modal-content help-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowHelpModal(false)}>×</button>
            <h2>How To Configure</h2>
            <ol className="help-instructions">
              <li>Your selected Ergosource products are tracked under the "Parts List" link in the top bar.</li>
              <li>Feel free to experiment! Try different configurations until you find exactly what you need.</li>
              <li>When your workbench configuration is ready, click the "Request Quote" button below the preview.</li>
              <li>You can configure as many workbenches as you'd like.</li>
            </ol>

            <h3>What's Next?</h3>
            <p className="help-text">
              After you complete your configuration and submit your quote request, our team will review your specifications.
              Once we receive your contact information through the quote form, we'll prepare a detailed price quote
              and send it to you along with a drawing of your configured workbench that includes a quote reference number.
            </p>

            <h3>To Purchase</h3>
            <p className="help-text">
              Simply contact us with your quote reference number (we'll have a copy of your configuration drawing)
              and we'll work with you to fulfill your order. Quick and easy!
            </p>

            <p className="help-footer">Have fun!</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Configurator;
